import { isArray, isEmpty, isNil } from 'lodash';
import { ReactElement, useEffect, useState } from 'react';
import {
  ControlProps,
  InputProps,
  MenuListProps,
  MenuProps,
  ValueContainerProps,
  components,
} from 'react-select';
import ExitIcon from '@shared/assets/icons/v2/exit.svg';
import { MBButton, MBIconButton } from '@shared/components/MBButton';
import { View } from '@shared/components/View';
import { MBTextBody2 } from '@shared/components/typography';
import { Colors, grid } from '@shared/styles';
import { useIsMobile } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import styles from '../styles.module.css';
/*
Note: these override components are named with the names expected by react-select.
Comments inline to explain what they do.
*/

// input area with selection pills
export function Control({ children, ...props }: ControlProps) {
  const selections = props.getValue();
  const isMobile = useIsMobile();
  const hasMultipleSelections = selections.length > 1;

  return (
    <>
      {!isEmpty(selections) && (
        <div className={styles.deselectLine}>
          <div>
            <MBTextBody2>
              <strong>{selections.length}</strong> {i18n('card.selection.selected')}
            </MBTextBody2>
          </div>
          <div onClick={props.clearValue} className={styles.deselectAll}>
            {hasMultipleSelections && (
              <MBTextBody2>{i18n('card.selection.deselectedAll')}</MBTextBody2>
            )}
            {!hasMultipleSelections && <MBTextBody2>{i18n('card.selection.deselect')}</MBTextBody2>}
          </div>
        </div>
      )}
      <components.Control
        {...props}
        getStyles={() => ({
          border: `1px solid ${props.isFocused ? Colors.v2.green30 : Colors.v2.gray10}`,
          paddingLeft: grid(0.5),
          paddingRight: grid(0.5),
          paddingTop: grid(0.5),
          paddingBottom: grid(0.5),
          borderRadius: 8,
          fontSize: `var(--text-body1-font-size)`,
          letterSpacing: `var(--text-body1-letter-spacing)`,
          color: Colors.v2.purple,
          marginBottom: grid(2),
          width: isMobile ? '100%' : 290,
        })}
      >
        {children}
      </components.Control>
    </>
  );
}

// options list container
export function Menu({ children, ...props }: MenuProps) {
  return (
    <components.Menu {...props} getStyles={() => ({})}>
      {children}
    </components.Menu>
  );
}

const PAGE_SIZE = 50;
// options list
export function MenuList(props: MenuListProps) {
  const [maxToShow, setMaxToShow] = useState(PAGE_SIZE);
  const existingStyles = props.getStyles('menuList', props);
  const inputValue = props.selectProps.inputValue;
  const onLoadMore = props.selectProps.onLoadMore;
  const hasServerMore = props.selectProps.hasServerMore;

  useEffect(() => {
    setMaxToShow(PAGE_SIZE);
  }, [inputValue]);

  let childrenToRender = props.children;
  let hasMoreLocal = false;
  const isChildrenArray = !isNil(props.children) && isArray(props.children);
  // When server pagination is in use, show all local children and rely solely on
  // hasServerMore / onLoadMore for the "Load more" button.
  const useServerPagination = !isNil(onLoadMore);
  if (isChildrenArray && !useServerPagination) {
    const childrenArray = props.children as ReactElement[];
    childrenToRender = childrenArray.slice(0, maxToShow);
    hasMoreLocal = childrenArray.length > maxToShow;
  }

  const showLoadMore = hasMoreLocal || (!useServerPagination ? false : hasServerMore);
  const handleLoadMore = hasMoreLocal ? () => setMaxToShow(prev => prev + PAGE_SIZE) : onLoadMore;

  return (
    <components.MenuList
      {...props}
      getStyles={() => ({
        ...existingStyles,
        marginLeft: -grid(2),
        marginRight: -grid(2),
        paddingBottom: grid(1),
      })}
    >
      {childrenToRender}
      {showLoadMore && (
        <View paddingVertical={2}>
          <MBButton
            title={i18n('card.list.loadMore')}
            size="small"
            theme="transparent"
            onPress={handleLoadMore}
            style={{ alignSelf: 'center' }}
          />
        </View>
      )}
    </components.MenuList>
  );
}

// display placeholder text
export function Input({ children, ...props }: InputProps) {
  const handleClearInput = () => {
    props.selectProps.onInputChange('', {
      action: 'input-change',
      prevInputValue: props.selectProps.inputValue,
    });
  };

  return (
    <View relative style={{ width: '100%' }}>
      <components.Input
        {...props}
        placeholder={i18n('common.searchDotDotDot')}
        getStyles={() => ({
          marginTop: props.hasValue && !props.selectProps.hideMultiValue ? grid(1) : 0,
          paddingTop: grid(0.5),
          paddingBottom: grid(0.5),
          paddingLeft: grid(1),
          paddingRight: grid(1),
          position: 'sticky',
          bottom: 0,
          background: Colors.v2.white,
        })}
      >
        {children}
      </components.Input>
      {!isEmpty(props.selectProps.inputValue) && (
        <View style={{ position: 'absolute', right: 5, bottom: 4 }}>
          <MBIconButton
            source={ExitIcon}
            onPress={handleClearInput}
            color={Colors.v2.purple}
            backgroundColor={Colors.v2.gray05}
            size="small"
            style={{ padding: 4 }}
          />
        </View>
      )}
    </View>
  );
}

export function ValueContainer({ children, ...props }: ValueContainerProps) {
  return (
    <components.ValueContainer
      {...props}
      getStyles={(key, styleProps) => {
        const originalStyles = props.getStyles(key, styleProps);
        return {
          ...originalStyles,
          maxHeight: '20vh',
          overflowX: 'hidden',
          overflowY: 'auto',
        };
      }}
    >
      {children}
    </components.ValueContainer>
  );
}

// components we don't need/want but that there's no props for removing
export function MultiValueRemove() {
  return null;
}
export function IndicatorsContainer() {
  return null;
}
export function Placeholder() {
  return null;
}
